// src/pages/RecPrototype.js
/* eslint-disable */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import HelmetTemplate from '../utils/HelmetTemplate';
import WebpageShell from './WebpageShell';

// Recommendation UI
import OptionWindow from '../components/Recommendations/OptionWindow';
import TabBar from '../components/Recommendations/TabBar';
import DesktopRecTabBar from '../components/Recommendations/DesktopRecTabBar';
import Loader from '../components/Loader';
import RecWelcomePopup from '../components/Popup/RecWelcomePopup';

// Utils
import { extractPathParam } from '../utils/UrlDataHandler';
import recUtils, { RecommendationState } from '../utils/RecUtils';
import { trackEvent } from '../utils/Tracking';


/** Renders only the active tab + neighbors. */
const VirtualizedTabContainer = React.forwardRef(
  (
    {
      options,
      activeIndex,
      swipeOffset,
      isTransitioning,
      contentRefs,
      handleContentScroll,
      isFallbackMode,
      isMobile,
      welcomePopupClosed,
    },
    ref
  ) => {
    const getVisibleTabIndices = useCallback(() => {
      const set = new Set([activeIndex]);
      if (activeIndex > 0) set.add(activeIndex - 1);
      if (activeIndex < options.length - 1) set.add(activeIndex + 1);
      return Array.from(set).sort((a, b) => a - b);
    }, [activeIndex, options.length]);

    const visible = getVisibleTabIndices();

    return (
      <div
        ref={ref}
        className={`flex ${isTransitioning ? 'transition-transform duration-300 ease-out' : 'transition-none'}`}
        style={{ transform: `translateX(${swipeOffset}%)` }}
      >
        {options.map((option, idx) =>
          visible.includes(idx) ? (
            <OptionWindow
              key={option.id}
              option={option}
              optionIndex={idx}
              isActive={idx === activeIndex}
              contentRef={(el) => (contentRefs.current[idx] = el)}
              onScroll={handleContentScroll(idx)}
              isFallbackMode={isFallbackMode}
              isMobile={isMobile}
              welcomePopupClosed={welcomePopupClosed}
            />
          ) : (
            <div key={`placeholder-${option.id}`} className="flex-none w-full" />
          )
        )}
      </div>
    );
  }
);

export default function RecPrototype() {
  // Routing
  const { recommendationId } = useParams();

  // Data/state
  const [options, setOptions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [underlinePosition, setUnderlinePosition] = useState(0);

  const [currentState, setCurrentState] = useState(RecommendationState.INITIAL);
  const [isFallbackMode, setIsFallbackMode] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [dataError, setDataError] = useState(null);

  // Loader + welcome popup
  const [showLoader, setShowLoader] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [welcomePopupClosed, setWelcomePopupClosed] = useState(false);
  const [popupToggles, setPopupToggles] = useState([]);

  // Swipe/transition
  const [isMobile, setIsMobile] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const containerRef = useRef(null);
  const contentRefs = useRef([]);

  // Touch helpers
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const touchMoved = useRef(false);
  const animationFrameRef = useRef(null);
  const pendingStateUpdates = useRef({});

  // Per-tab scroll (optional; harmless if content is page-scrolling)
  const [tabStates, setTabStates] = useState([]);

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Cleanup RAF
  useEffect(() => () => animationFrameRef.current && cancelAnimationFrame(animationFrameRef.current), []);

  const handleLoaderComplete = () => {
    setShowLoader(false);
    setShowWelcomePopup(true);
  };
  const handleWelcomePopupClose = () => {
    setShowWelcomePopup(false);
    setWelcomePopupClosed(true);
  };

  // Boot state machine
  useEffect(() => {
    const recId = extractPathParam(0, '/recommendations/') || recommendationId;
    if (!recId) {
      setDataError('No recommendation ID found in URL');
      setDataReady(true);
      setIsLoading(false);
      return;
    }

    recUtils.initializeStateMachine(recId, async (newState, fallbackMode) => {
      setCurrentState(newState);
      setIsFallbackMode(fallbackMode);

      if (newState === RecommendationState.COMPLETED) {
        setDataReady(true);
        if (fallbackMode) {
          await handleFallbackMode();
        } else {
          await loadRecommendationData();
        }
      }
    });

    return () => recUtils.cleanup();
  }, [recommendationId]);

  // Track view once shown
  const hasTrackedViewRef = useRef(false);
  useEffect(() => {
    if (!showLoader && !hasTrackedViewRef.current) {
      (async () => {
        try {
          const evt = isFallbackMode ? 'recommendations_viewed_fallback' : 'recommendations_viewed';
          await trackEvent(evt, { recommendation_id: recommendationId || 'unknown' });
          hasTrackedViewRef.current = true;
        } catch (err) {
          console.error('Error tracking recommendations view:', err);
        }
      })();
    }
  }, [showLoader, isFallbackMode, recommendationId]);

  // Data loaders
  const loadRecommendationData = async () => {
    try {
      const recId = extractPathParam(0, '/recommendations/') || recommendationId;
      if (!recId) throw new Error('No recommendation ID');

      const ok = await recUtils.fetchRecommendationFromId(recId);
      if (!ok) throw new Error(recUtils.getError());

      const { userToggle1, userToggle2 } = recUtils.getUserInputToggles();
      setPopupToggles([userToggle1, userToggle2].filter(Boolean));

      const status = recUtils.getStatus();
      if (!(status === 1 || status === 'completed OK')) {
        throw new Error(`Recommendation not ready. Status: ${status}`);
      }

      const count = recUtils.getResultCount();
      if (!count || count <= 0) throw new Error('No results found in recommendation');

      const results = await recUtils.fetchAllClinicData();
      if (!results) throw new Error(recUtils.getError());

      const formatted = results.map((r) => recUtils.getFormattedOptionData(r));
      setOptions(formatted);
      setTabStates(formatted.map(() => ({ scrollPosition: 0 })));
      setIsLoading(false);
    } catch (err) {
      setDataError(err.message || 'Failed to load recommendation');
      setIsLoading(false);
    }
  };

  const handleFallbackMode = async () => {
    try {
      const fallback = await recUtils.getFallbackClinicData();
      if (!fallback || fallback.length === 0) throw new Error('Failed to load fallback clinic data');

      const formatted = fallback.map((r) => recUtils.getFormattedOptionData(r));
      setOptions(formatted);
      setTabStates(formatted.map(() => ({ scrollPosition: 0 })));
      setIsLoading(false);
    } catch (err) {
      setDataError(err.message || 'Error in fallback mode');
      setIsLoading(false);
    }
  };

  // --- Tab navigation helpers ---
  const saveCurrentTabState = useCallback((tabIndex) => {
    const el = contentRefs.current[tabIndex];
    if (el) {
      const scrollPosition = el.scrollTop;
      setTabStates((prev) => {
        const copy = [...prev];
        copy[tabIndex] = { scrollPosition };
        return copy;
      });
    }
  }, []);

  const restoreTabState = useCallback(
    (tabIndex) => {
      const el = contentRefs.current[tabIndex];
      const state = tabStates[tabIndex];
      if (el && state) setTimeout(() => (el.scrollTop = state.scrollPosition), 50);
    },
    [tabStates]
  );

  const handleTabClick = (index) => {
    if (isTransitioning || index === activeIndex || isLoading) return;
    saveCurrentTabState(activeIndex);
    setIsTransitioning(true);
    setActiveIndex(index);
    setUnderlinePosition(index * (100 / options.length));
    setSwipeOffset(-index * 100);
    setTimeout(() => {
      restoreTabState(index);
      setIsTransitioning(false);
    }, 300);
  };

  const handleContentScroll = (index) => (e) => {
    if (index !== activeIndex) return;
    const scrollTop = e.target.scrollTop;
    setTabStates((prev) => {
      const copy = [...prev];
      copy[index] = { scrollPosition: scrollTop };
      return copy;
    });
  };

  // --- Swipe logic ---
  const batchStateUpdates = useCallback(() => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(() => {
      const u = pendingStateUpdates.current;
      if (u.swipeOffset !== undefined) setSwipeOffset(u.swipeOffset);
      if (u.underlinePosition !== undefined) setUnderlinePosition(u.underlinePosition);
      pendingStateUpdates.current = {};
      animationFrameRef.current = null;
    });
  }, []);

  const handleTouchStart = useCallback(
    (e) => {
      if (isTransitioning || isLoading) return;
      const t = e.touches[0];
      touchStartX.current = t.clientX;
      touchStartY.current = t.clientY;
      touchStartTime.current = Date.now();
      touchMoved.current = false;

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      pendingStateUpdates.current = {};
    },
    [isTransitioning, isLoading]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (isTransitioning || isLoading) return;
      const t = e.touches[0];
      const dx = t.clientX - touchStartX.current;
      const dy = t.clientY - touchStartY.current;

      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
        touchMoved.current = true;
        e.preventDefault();

        const sw = window.innerWidth;
        const prog = dx / sw;
        const newOffset = -activeIndex * 100 + prog * 100;
        const min = -(options.length - 1) * 100;
        const max = 0;
        const clamped = Math.max(min, Math.min(max, newOffset));

        pendingStateUpdates.current = {
          swipeOffset: clamped,
          underlinePosition: -clamped / options.length,
        };
        batchStateUpdates();
      }
    },
    [isTransitioning, isLoading, activeIndex, options.length, batchStateUpdates]
  );

  const handleTouchEnd = useCallback(
    (e) => {
      if (!touchMoved.current || isTransitioning || isLoading) return;

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        const u = pendingStateUpdates.current;
        if (u.swipeOffset !== undefined) setSwipeOffset(u.swipeOffset);
        if (u.underlinePosition !== undefined) setUnderlinePosition(u.underlinePosition);
        pendingStateUpdates.current = {};
        animationFrameRef.current = null;
      }

      setIsTransitioning(true);

      const t = e.changedTouches[0];
      const dx = t.clientX - touchStartX.current;
      const dy = t.clientY - touchStartY.current;
      const dt = Date.now() - touchStartTime.current;

      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      const velocity = absX / dt;

      let target = activeIndex;
      const MIN_DIST = 50;
      const MAX_TIME = 300;
      const MIN_VEL = 0.3;

      if (absX > absY && (absX >= MIN_DIST || (velocity >= MIN_VEL && dt <= MAX_TIME))) {
        target = dx > 0 ? Math.max(0, activeIndex - 1) : Math.min(options.length - 1, activeIndex + 1);
      }

      if (target !== activeIndex) saveCurrentTabState(activeIndex);

      setActiveIndex(target);
      setSwipeOffset(-target * 100);
      setUnderlinePosition(target * (100 / options.length));

      if (target !== activeIndex) restoreTabState(target);

      setTimeout(() => setIsTransitioning(false), 300);
    },
    [isTransitioning, isLoading, activeIndex, options.length, saveCurrentTabState, restoreTabState]
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Keep arrays sized
  useEffect(() => {
    contentRefs.current = contentRefs.current.slice(0, options.length);
    setTabStates((prev) =>
      prev.length !== options.length ? options.map((_, i) => prev[i] || { scrollPosition: 0 }) : prev
    );
  }, [options.length]);

  // ----------------- Render -----------------
  if (showLoader) {
    return (
      <WebpageShell
        navProps={{ addAnnouncementBar: false, isRecPrototypePage: true }}
        showCTA={true}
        showFooter={true}
      >
        <div className="min-h-[60vh] md:min-h-[70vh] flex items-center justify-center">
          <Loader dataReady={dataReady} onLoaderComplete={handleLoaderComplete} />
        </div>
      </WebpageShell>
    );
  }

  if (dataError) {
  return (
    <WebpageShell
      navProps={{
        // NEW: drive the embedded mobile TabBar inside NavigationBar
       isRecPrototypePage: true,
        isTabBar: true,
        options,
        activeIndex,
        underlinePosition,
        onTabClick: handleTabClick,
      }}
      showCTA={true}
      showFooter={true}
    >
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="text-center max-w-md px-4">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Error Loading Data</h2>
            <p className="text-gray-600 mb-4">{dataError}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-mm_blue hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              Retry
            </button>
          </div>
        </div>
      </WebpageShell>
    );
  }

  if (options.length === 0) {
    return (
      <WebpageShell
        navProps={{ addAnnouncementBar: false, isRecPrototypePage: true }}
        showCTA={true}
        showFooter={true}
      >
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="text-center max-w-md px-4">
            <h2 className="text-xl font-bold text-gray-800 mb-2">No Results Found</h2>
            <p className="text-gray-600">
              We couldn&apos;t find any recommendations for you at this time.
            </p>
          </div>
        </div>
      </WebpageShell>
    );
  }

  return (
    <WebpageShell
      navProps={{
        addAnnouncementBar: false,
        isRecPrototypePage: true,
        // drive the embedded mobile TabBar inside NavigationBar
        isTabBar: true,
        options,
        activeIndex,
        underlinePosition,
        onTabClick: handleTabClick,
      }}
      showCTA={true}
      showFooter={true}
    >
      <HelmetTemplate
        title="Recommendations - Match Medic"
        description="Your personalized clinic recommendations from Match Medic"
        canonicalUrl={`https://matchmedic.com/recommendations/${recommendationId}`}
      />

      {/* Welcome popup */}
      <RecWelcomePopup
        isOpen={showWelcomePopup}
        onClose={handleWelcomePopupClose}
        optionCount={options.length}
        toggles={popupToggles}
      />

     {/* Desktop tab bar stays below the nav; mobile tab bar is now inside the nav */}
     <div className={`z-20 bg-white ${isMobile ? 'hidden' : 'sticky top-6'}`}>
       <div className="px-4 md:px-8">
         <DesktopRecTabBar
           options={options}
           activeIndex={activeIndex}
           onTabClick={handleTabClick}
         />
       </div>
     </div>

      {/* Swipeable content */}
      <div ref={containerRef} className="overflow-hidden">
        <VirtualizedTabContainer
          options={options}
          activeIndex={activeIndex}
          swipeOffset={swipeOffset}
          isTransitioning={isTransitioning}
          contentRefs={contentRefs}
          handleContentScroll={handleContentScroll}
          isFallbackMode={isFallbackMode}
          isMobile={isMobile}
          welcomePopupClosed={welcomePopupClosed}
        />
      </div>
    </WebpageShell>
  );
}
